import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { LucideIcon } from 'lucide-react';
import {
  Edit,
  Trash2,
  Film,
  Tv,
  Clapperboard,
  Volleyball,
  MicVocal,
  Antenna,
  Radio,
  GripVertical,
  Plus,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { categoryTypes } from '@/constants';
import {
  useTranslation,
  usePermissions,
  useCategories,
  useDeleteCategory,
  useToggleCategoryStatus,
  categoryKeys,
  useConfirmDialog,
} from '@/hooks';
import { Category, CategoryType } from '@/types';
import Button from '@/components/ui/Button';
import Toggle from '@/components/ui/Toggle';
import { PermissionGate } from '@/components/ui/PermissionGate';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import ImageNotAvailable from '@/assets/image-not-available.png';
import { categoryService } from '@/services/categoryService';
import toast from 'react-hot-toast';
import { ApiClientError } from '@/services/apiClient';
import { useBranding } from '@/context/BrandingContext';

const categoryTypeIcons: Record<CategoryType, LucideIcon> = {
  MOVIE: Film,
  TV_SHOW: Tv,
  DOCUMENTARY: Clapperboard,
  SPORTS: Volleyball,
  MUSIC: MicVocal,
  TV_CHANNEL: Antenna,
  RADIO_STATION: Radio,
};

const categoryTypeClass: Record<CategoryType, string> = {
  MOVIE: 'bg-blue-100 text-blue-800',
  TV_SHOW: 'bg-purple-100 text-purple-800',
  DOCUMENTARY: 'bg-rose-100 text-rose-800',
  SPORTS: 'bg-green-100 text-green-800',
  MUSIC: 'bg-yellow-100 text-yellow-800',
  TV_CHANNEL: 'bg-indigo-100 text-indigo-800',
  RADIO_STATION: 'bg-cyan-100 text-cyan-800',
};

interface CategoriesTableProps {
  categories: Category[];
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onToggleStatus: (category: Category, active: boolean) => void;
  canEdit: boolean;
  canDelete: boolean;
  isReordering: boolean;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => Promise<void>;
  activeId: string | null;
  toggleDisabled: boolean;
}

const CategoriesTable = ({
  categories,
  onEdit,
  onDelete,
  onToggleStatus,
  canEdit,
  canDelete,
  isReordering,
  onDragStart,
  onDragEnd,
  activeId,
  toggleDisabled,
}: CategoriesTableProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );
  const { t } = useTranslation();

  const SortableRow = ({ category }: { category: Category }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const badgeClass = categoryTypeClass[category.type] || 'bg-gray-100 text-gray-800';

    return (
      <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
        <td className="px-3 py-4 text-gray-400 cursor-grab align-middle w-12" {...attributes} {...listeners}>
          <GripVertical size={18} />
        </td>
        <td className="px-4 py-4 text-sm text-gray-900 align-middle w-20">
          {typeof category.displayOrder === 'number' ? category.displayOrder : '—'}
        </td>
        <td className="px-6 py-4 align-middle">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              <img
                src={category.imageUrl || ImageNotAvailable}
                alt={category.description}
                className="h-full w-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                }}
              />
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{category.description}</div>
              <div className="text-xs text-gray-500">
                {category.englishDescription}
                {category.sportsContent && (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded ml-2">
                    Contenido Deportivo
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 align-middle w-40">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
            {t.categories.types[category.type]}
          </span>
        </td>
        <td className="px-6 py-4 align-middle w-36">
          <PermissionGate
            permission="CATEGORY_TOGGLE_STATUS"
            fallback={
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  category.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {category.active ? t.common.active : t.common.inactive}
              </span>
            }
          >
            <Toggle
              checked={category.active}
              onChange={(checked) => onToggleStatus(category, checked)}
              disabled={toggleDisabled || isReordering}
              size="sm"
            />
          </PermissionGate>
        </td>
        <td className="px-6 py-4 w-40 align-middle">
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(category)}>
                <Edit size={14} />
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="danger" onClick={() => onDelete(category)} disabled={isReordering}>
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const activeCategory = categories.find((category) => category.id === activeId) || null;

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                {t.categories.fields.displayOrder}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t.categories.fields.description}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                {t.categories.fields.type}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                {t.categories.fields.active}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                {t.common.actions}
              </th>
            </tr>
          </thead>
          <SortableContext items={categories.map((category) => category.id)} strategy={verticalListSortingStrategy}>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <SortableRow key={category.id} category={category} />
              ))}
            </tbody>
          </SortableContext>
        </table>
        <DragOverlay>
          {activeCategory ? (
            <div className="flex items-center gap-3 bg-white shadow rounded px-3 py-2 border">
              <GripVertical size={16} className="text-gray-400" />
              <span className="text-sm text-gray-800 font-medium truncate max-w-[240px]">
                {activeCategory.description}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {categories.length === 0 && (
        <div className="px-6 py-8 text-center text-gray-500">
          {t.categories.messages.noData}
        </div>
      )}
    </div>
  );
};

const Categories = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { confirm, dialog } = useConfirmDialog();
  const { branding, isLoading: isBrandingLoading } = useBranding();
  const modules = branding.modules ?? {};
  const isModuleEnabled = (enabled?: boolean) => enabled === true;

  const urlType = searchParams.get('type');
  const defaultType: CategoryType = categoryTypes.includes(urlType as CategoryType) ? (urlType as CategoryType) : 'MOVIE';
  const [activeTab, setActiveTab] = useState<CategoryType>(defaultType);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  const enabledTypes = useMemo(
    () =>
      categoryTypes.filter((type) => {
        switch (type) {
          case 'MOVIE':
            return isModuleEnabled(modules.movies);
          case 'TV_SHOW':
            return isModuleEnabled(modules.tvShows);
          case 'DOCUMENTARY':
            return isModuleEnabled(modules.documentaries);
          case 'SPORTS':
            return isModuleEnabled(modules.sports);
          case 'MUSIC':
            return isModuleEnabled(modules.concerts);
          case 'TV_CHANNEL':
            return isModuleEnabled(modules.tvChannels);
          case 'RADIO_STATION':
            return isModuleEnabled(modules.radioStations);
          default:
            return false;
        }
      }),
    [modules]
  );
  const hasEnabledTypes = enabledTypes.length > 0;
  const fallbackType = enabledTypes[0];

  const queryParams = useMemo(
    () => ({
      type: activeTab,
      _size: 1000,
      _page: 0,
      _sort: '+displayOrder',
    }),
    [activeTab]
  );

  const { data, isLoading, isError, error, refetch } = useCategories(queryParams, {
    enabled: hasEnabledTypes && !isBrandingLoading,
  });

  const deleteCategoryMutation = useDeleteCategory();
  const toggleStatusMutation = useToggleCategoryStatus();

  useEffect(() => {
    if (isBrandingLoading || !hasEnabledTypes) {
      return;
    }

    if (urlType && enabledTypes.includes(urlType as CategoryType)) {
      if (urlType !== activeTab) {
        setActiveTab(urlType as CategoryType);
      }
      return;
    }

    if (fallbackType && activeTab !== fallbackType) {
      setActiveTab(fallbackType);
    }
    if (fallbackType && urlType !== fallbackType) {
      setSearchParams({ type: fallbackType });
    }
  }, [urlType, activeTab, enabledTypes, fallbackType, hasEnabledTypes, isBrandingLoading, setSearchParams]);

  useEffect(() => {
    if (!hasEnabledTypes) {
      setCategories([]);
      return;
    }

    if (data?.content) {
      const ordered = [...data.content].sort((a, b) => {
        const orderA = typeof a.displayOrder === 'number' ? a.displayOrder : Number.MAX_SAFE_INTEGER;
        const orderB = typeof b.displayOrder === 'number' ? b.displayOrder : Number.MAX_SAFE_INTEGER;
        return orderA - orderB || a.description.localeCompare(b.description);
      });
      setCategories(ordered);
    } else {
      setCategories([]);
    }
  }, [data]);

  const tabs = useMemo(
    () =>
      enabledTypes.map((type) => ({
        type,
        icon: categoryTypeIcons[type],
        label: t.categories.types[type as keyof typeof t.categories.types] || type,
      })),
    [enabledTypes, t]
  );

  const canCreate = hasPermission('CATEGORY_CREATE');
  const canEdit = hasPermission('CATEGORY_UPDATE');
  const canDelete = hasPermission('CATEGORY_DELETE');

  const handleCreate = () => {
    navigate(`/content/categories/create?type=${activeTab}`);
  };

  const handleEdit = (category: Category) => {
    navigate(`/content/categories/${category.id}/edit`);
  };

  const handleDelete = async (category: Category) => {
    const attemptDelete = async (force = false, message?: string) => {
      try {
        const confirmed = await confirm({
          title: t.common.confirmationTitle,
          message: message || t.categories.messages.confirmDelete,
          confirmLabel: t.buttons.delete,
          cancelLabel: t.buttons.cancel,
          confirmVariant: 'danger',
          onConfirm: async () => {
            await deleteCategoryMutation.mutateAsync({ id: category.id, force });
          },
        });

        if (!confirmed) {
          return;
        }

        toast.success(t.categories.messages.deleted);
      } catch (err) {
        if (!force && err instanceof ApiClientError && err.status === 400) {
          const serverMessage = err.data?.message || err.message || t.errors.unexpectedError;
          await attemptDelete(true, serverMessage);
          return;
        }

        console.error('Delete category error:', err);
        toast.error(err instanceof Error ? err.message : t.errors.unexpectedError);
      }
    };

    await attemptDelete(false);
  };

  const handleToggleStatus = async (category: Category, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ id: category.id, active });
    } catch (err) {
      console.error('Toggle status error:', err);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const current = [...categories];
    const previousOrders = new Map(current.map((category) => [category.id, category.displayOrder]));
    const oldIndex = current.findIndex((category) => category.id === active.id);
    const newIndex = current.findIndex((category) => category.id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const movedList = arrayMove(current, oldIndex, newIndex).map((category, index) => ({
      ...category,
      displayOrder: index + 1,
    }));
    const updates = movedList.filter((category) => previousOrders.get(category.id) !== category.displayOrder);

    setCategories(movedList);
    setIsReordering(true);

    try {
      if (updates.length > 0) {
        await Promise.all(
          updates.map((category) =>
            typeof category.displayOrder === 'number'
              ? categoryService.updateCategoryDisplayOrder(category.id, category.displayOrder)
              : Promise.resolve()
          )
        );
      }
      await queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
    } catch (err) {
      console.error('Error updating category order', err);
      await refetch();
    } finally {
      setIsReordering(false);
    }
  };

  const renderContent = () => {
    if (isBrandingLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    if (!hasEnabledTypes) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-sm text-yellow-700">{t.categories.messages.noData}</p>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error instanceof Error ? error.message : t.errors.loadingError}</p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              {t.buttons.retry}
            </Button>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
      <CategoriesTable
        categories={categories}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        canEdit={canEdit}
        canDelete={canDelete}
        isReordering={isReordering}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        activeId={activeId}
        toggleDisabled={toggleStatusMutation.isPending}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {hasEnabledTypes && (
          <div className="bg-white rounded-lg shadow">
            <nav className="flex overflow-x-auto" aria-label="Category types">
              {tabs.map(({ type, icon: Icon, label }) => {
                const isActive = type === activeTab;

                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      if (type !== activeTab) {
                        setActiveTab(type);
                        setSearchParams({ type });
                      }
                    }}
                    className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      isActive
                        ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">{t.categories.listTitle}</h2>
            <p className="text-gray-500">{t.categories.description}</p>
          </div>
          {canCreate && hasEnabledTypes && (
            <Button onClick={handleCreate} className="flex items-center gap-2">
              <Plus size={16} />
              {t.categories.actions.create}
            </Button>
          )}
        </div>

        {renderContent()}
        {dialog}
      </div>
    </div>
  );
};

const ProtectedCategories = () => (
  <ProtectedRoute anyPermission={['CATEGORY_READ', 'CATEGORY_CREATE', 'CATEGORY_UPDATE', 'CATEGORY_DELETE']}>
    <Categories />
  </ProtectedRoute>
);

export default ProtectedCategories;
